import { ROUTES } from "@/constants/routes";
import type { useAppNavigate } from "@/hooks/pane-routing";
import { emitCanvasExpandNode } from "@/pages/canvas/constants/canvasExpandNode.constants";
import { emitCanvasOpenFloatingSession } from "@/pages/canvas/context/CanvasFloatingChatContext";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import type { CanvasEntity } from "@cohesive/api-entities";
import {
  type BlockDisplayRecord,
  type Entity,
  getEntityData,
  isBlockNodeData,
  legacyFileEntityDataToFileRecord,
} from "@cohesive/ui-components";
import type { Node } from "@xyflow/react";
import { isCanvasLinkEntity } from "./canvasLinkEntity";
import { resolveGenericEntityPaneTarget } from "./genericEntityPane";

type AppNavigateFn = ReturnType<typeof useAppNavigate>;

export interface OpenEntityOptions {
  /** Opens a canvas card's linked canvas as a workspace tab
   *  (`useOpenCanvasLink`). A canvas has no pane view, so without it a
   *  canvas link is a no-op here. */
  openCanvasLink?: (entity: CanvasEntity) => void;
  /** The canvas's file record for a node's file id, so a block is told
   *  apart from a document the way the node router does: by the record's
   *  display first, the node's copy only while the record is not in. */
  fileRecordById?: (id: string) => BlockDisplayRecord | null | undefined;
}

function openLegacyEntityRecord(
  record: FileRecord,
  appNavigate: AppNavigateFn,
) {
  appNavigate({
    pane: "rightPane",
    to: route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId: record.id }),
    options: { state: { fileRecord: record } },
  });
}

/**
 * Opens an entity's detail pane in the right panel.
 * Shared routing map used by canvas explorer, connectors rail, and chat mentions.
 */
export function openEntityInDetailPane(
  entity: Entity,
  appNavigate: AppNavigateFn,
  options?: OpenEntityOptions,
) {
  const entityData = getEntityData(entity);
  if (!entityData?.id) return;

  const targetPane = "rightPane";

  // A canvas card opens the linked canvas as a workspace tab — there is no
  // pane view of a canvas.
  if (isCanvasLinkEntity(entity)) {
    options?.openCanvasLink?.(entity);
    return;
  }

  if ("type" in entity && entity.type === "skill") {
    appNavigate({
      pane: targetPane,
      to: route(ROUTES.SKILLS.SHOW, { id: entityData.id.toString() }),
    });
    return;
  }

  const isNotionDocument =
    ("type" in entity && entity.type === "notion_mcp_document") ||
    ("base" in entity &&
      entity.base === "document" &&
      entity.platform === "notion");

  if (isNotionDocument) {
    appNavigate({
      pane: targetPane,
      to: route(ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL, {
        pageId: entityData.id.toString(),
      }),
      options: { state: { entity } },
    });
    return;
  }

  if (
    "base" in entity &&
    (entity.base === "email" || entity.base === "email_thread") &&
    entity.platform === "gmail"
  ) {
    appNavigate({
      pane: targetPane,
      to: route(ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL, {
        emailId: entityData.id.toString(),
      }),
      options: { state: { entity } },
    });
    return;
  }

  if (
    "base" in entity &&
    entity.platform === "github" &&
    (entity.base === "issue" ||
      ("type" in entity && entity.type === "github_project_item"))
  ) {
    appNavigate({
      pane: targetPane,
      to: route(ROUTES.GITHUB.ISSUE_DETAIL, {
        issueId: entityData.id.toString(),
      }),
      options: { state: { entity } },
    });
    return;
  }

  if (
    "base" in entity &&
    entity.platform === "github" &&
    entity.base === "pull_request"
  ) {
    appNavigate({
      pane: targetPane,
      to: route(ROUTES.GITHUB.PR_DETAIL, {
        prId: entityData.id.toString(),
      }),
      options: { state: { entity } },
    });
    return;
  }

  // Legacy `canvas_file` (rag_files-backed) uploads — explicit no-op.
  if (
    "base" in entity &&
    entity.base === "file" &&
    ((entity as any).platform === "cohesive" ||
      (entity as any).type === "canvas_file")
  ) {
    return;
  }

  // Legacy image/video — adapt and open via unified FileRecordDetailPane.
  if (
    "base" in entity &&
    (entity.base === "image" || entity.base === "video")
  ) {
    const record = legacyFileEntityDataToFileRecord(
      entity.base as "image" | "video",
      entityData as Record<string, unknown>,
    );
    if (record) {
      openLegacyEntityRecord(record, appNavigate);
      return;
    }
  }

  // Google Drive files get their own dedicated pane with native previews.
  if (
    "base" in entity &&
    entity.platform === "google-drive" &&
    entity.base === "file"
  ) {
    appNavigate({
      pane: targetPane,
      to: route(ROUTES.GOOGLE_DRIVE.FILE_DETAIL, {
        fileId: entityData.id.toString(),
      }),
      options: { state: { entity } },
    });
    return;
  }

  // Generic entities carry their real platform in data.platform_name — route to
  // that platform's dedicated pane. Unmapped platforms fall through to the
  // generic entity detail pane below.
  if ("base" in entity && entity.base === "generic") {
    const target = resolveGenericEntityPaneTarget(entity);
    if (target) {
      appNavigate({
        pane: targetPane,
        to: target.to,
        options: { state: target.state },
      });
      return;
    }
  }

  // External `base: "file"` (Dropbox, etc.) — fall through to generic entity detail pane.
  // (canvas_file rag-files are already no-op'd above.)

  appNavigate({
    pane: targetPane,
    to: route(ROUTES.ENTITIES.DETAIL, {
      platform: entity.platform || "cohesive",
      entityId: entityData.id.toString(),
    }),
    options: { state: { entity } },
  });
}

/**
 * Opens a canvas node's detail pane in the right panel.
 * Mirrors EntityNode.handleEntityClick's routing map so the explorer
 * can open the same detail views without going through the canvas node itself.
 *
 * Non-entity node types (textNote, canvasText, canvasShape, etc.) are silently
 * ignored — they have no dedicated detail pane.
 */
export function openNodeInDetailPane(
  node: Node,
  appNavigate: AppNavigateFn,
  canvasId?: string,
  options?: OpenEntityOptions,
) {
  // Comment markers are a server-written projection with no UI of their own.
  if (node.type === "commentMarker") return;

  if (node.type === "chatSession") {
    const flowSessionId = (node.data as any)?.flowSessionId as
      | string
      | undefined;
    if (flowSessionId && canvasId) {
      emitCanvasOpenFloatingSession({
        canvasId,
        flowSessionId,
      });
    } else if (flowSessionId) {
      // canvasId not available — fall back to expanding inline on the canvas
      window.dispatchEvent(
        new CustomEvent("canvas:open-quick-chat", {
          detail: { nodeId: node.id },
        }),
      );
    }
    return;
  }

  if (node.type !== "entity") return;

  // A block has no detail pane: its full-size surface is the lightbox on
  // its node, which the mounted canvas opens. (Unacknowledged — no canvas
  // mounted — it falls through to the pane, which redirects to the canvas.)
  const entity = (node.data as any)?.entity;
  const fileRecordId = entity?.data?.id as string | undefined;
  if (
    isBlockNodeData(
      node.data,
      fileRecordId ? options?.fileRecordById?.(fileRecordId) : undefined,
    ) &&
    emitCanvasExpandNode(node.id)
  ) {
    return;
  }

  if (!entity) return;

  openEntityInDetailPane(entity, appNavigate, options);
}
